import * as React from 'react';
import { useState } from 'react';
import { css, Global, ThemeProvider } from '@emotion/react';
import { HashRouter, Navigate, Route, Routes } from 'react-router-dom';
import { DarkTheme } from '../theme/DarkTheme';
import { SystemContext, useSystem } from '../hooks/useSystem';
import { LoginPage } from './signed-out/LoginPage';
import { System } from '../core/System';
import { SquareFont } from '../fonts';
import { RegisterPage } from './signed-out/RegisterPage';
import { observer } from 'mobx-react';
import { HomePage } from './signed-in/HomePage';
import { NotificationStoreWidget } from '../widgets/stores/NotificationStoreWidget';
import { SCROLL } from '../widgets/scroll';
import { ForgotPasswordPage } from './signed-out/ForgotPasswordPage';
import { ResetPasswordPage } from './signed-out/ResetPasswordPage';
import { LightBoxContext, LightBoxWidget } from '../widgets/layers/LightBoxWidget';
import { Routing } from './Routing';
import { DialogStoreWidget } from '../widgets/stores/DialogStoreWidget';
import { ImageModel } from '../core/models/ImageModel';

export interface RootWidgetProps {
  system: System;
}

export const Redirect = observer(() => {
  const system = useSystem();
  return system.user ? <Navigate to={Routing.HOME} /> : <Navigate to={Routing.SIGN_IN} />;
});

export const RootPage: React.FC<RootWidgetProps> = observer((props) => {
  const theme = DarkTheme;

  let background = css`
    html,
    body,
    #application {
      background: ${theme.layout.background};
    }
  `;
  const [image, setImage] = useState<ImageModel | null>(null);

  return (
    <SystemContext.Provider value={props.system}>
      <LightBoxContext.Provider
        value={{
          setImage,
          clearImage: () => {
            setImage(null);
          }
        }}
      >
        <Global styles={[S.Global, background]} />
        <ThemeProvider theme={theme}>
          <NotificationStoreWidget />
          <HashRouter>
            <Routes>
              <Route path="/" element={<Redirect />} />
              <Route path={`${Routing.HOME}/*`} element={<HomePage />} />
              <Route path={Routing.SIGN_IN} element={<LoginPage />} />
              <Route path={Routing.FORGOT_PASSWORD} element={<ForgotPasswordPage />} />
              <Route path={Routing.RESET_PASSWORD} element={<ResetPasswordPage />} />
              <Route path={Routing.REGISTER} element={<RegisterPage />} />
            </Routes>
            <LightBoxWidget image={image} />
          </HashRouter>
          <DialogStoreWidget />
        </ThemeProvider>
      </LightBoxContext.Provider>
    </SystemContext.Provider>
  );
});
namespace S {
  export const Global = css`
    * {
      margin: 0;
      padding: 0;
      user-select: none;
      ${SCROLL};
      text-decoration: none;
      -webkit-tap-highlight-color: transparent;
    }

    html {
      overflow: hidden;
    }

    html,
    body,
    #application {
      height: 100%;
      width: 100%;
      overscroll-behavior-y: none;
      position: fixed;
    }

    ${SquareFont};
  `;
}
